import {Button, Tooltip} from "antd";
import jsPDF from "jspdf";
import SiteReportPopup from "../../../../Modal/Modal-Content/SiteReportPopup";
import temperatureImage from "../../../../../../../assets/img/report/site-report/site-report-thermometer.png"
import secondaryHeaderImage from "../../../../../../../assets/img/report/site-report/site-report-header-3d-views.png"
import northBanner from "../../../../../../../assets/img/report/site-report/site-report-looking-north-banner.png"
import westBanner from "../../../../../../../assets/img/report/site-report/site-report-looking-west-banner.png"
import eastBanner from "../../../../../../../assets/img/report/site-report/site-report-looking-east-banner.png"
import treeBanner from "../../../../../../../assets/img/report/site-report/site-report-header-header=tree-height.png"
import headerSiteImage from "../../../../../../../assets/img/report/site-report/site-report-header.png"
import {useViewerStore} from '../../../../../../../store/store';
import {GetRoundNum} from '../../../../Three-JS-Viewer/Controllers/Common';
import {logger} from '../../../../../../../services/loggingService';
import {FOOT, METER, UnitContextType} from '../../../../../../../contexts/UnitContext';
import {DownloadIcon} from "@phosphor-icons/react";
import { roundedBasedOn33Percent } from '../../../../Popout-Window/Roof-Square-Popout/Roof Square/RoofSquareTable';
import {useAreChangesMadeToDesign} from "../../../../../../../hooks/useAreChangesMadeToDesign";
import {ViewerState} from "../../../../../../../@types/store";
import {BuildingRoofSquares} from "../../../../../../../@types/types";
import { monthlyEstimateMonths } from '../../../../../../../Constants';
import { AlertType } from "../../../../../../../@types/enums";

interface allTreeDataType {
    treeName: string;
    treeHeight: number;
    treeDiameter: number;
}

function SiteReportPdfGenerator() {

    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const { areChangesMade, isOriginalSite } = useAreChangesMadeToDesign();

    const openSiteReportPopup = () => {
        useViewerStore.setState({isLeftSidebarOpen: false});

        sceneViewer.viewerAlert({
            show: true,
            title: "Download Site Report",
            message: <SiteReportPopup/>,
            messageType: AlertType.info,
            isModal: true
        })
    }

    return (
        <Tooltip title={areChangesMade ? "Changes were made. Finalize before downloading."
                            : isOriginalSite
                            ? "Feature disabled for the Original design. Select a finalized design."
                            : ""}>
            <Button
                disabled={areChangesMade || isOriginalSite}
                icon={<DownloadIcon size={20}/>}
                type='primary' size='large' block onClick={openSiteReportPopup}>Generate Site Report</Button> 
        </Tooltip>
           
    );
}

export default SiteReportPdfGenerator;

export const generateSiteReport = async (sceneViewer : any, viewerState : ViewerState, unitContext : UnitContextType) => {
    const {
        siteAddress,
        activeModule,
        efficiency,
        efficiencyOverride,
        modeCount,
        modeCircleSolar,
        roofInfoArr,
        accessoryBuildingList,
        selectedLossesVendor,
        isHideAllGhostTrees,
        roofSquareTableData,
        useJune2026SquareCalculation
    } = viewerState;
    const {convertQuantityToUnit} = unitContext;
    const wattage = activeModule?.power ?? 0;

    const siteReportImages = await sceneViewer.getImagesForSiteReport();

    const homeImage = siteReportImages.home;

    const accessoryRoofs : string[]  = [];
    for (const roof of sceneViewer.roofMeshArr) {
        if (accessoryBuildingList.includes(roof.userData.buildingName)) {
            accessoryRoofs.push(roof.roofFaceId);
        }
    }

    const productionNumFormat = new Intl.NumberFormat('en-US', {maximumFractionDigits: 0});
    const arraySizeNumFormat = new Intl.NumberFormat('en-US', {minimumFractionDigits: 3, maximumFractionDigits: 3});
    const hasBallastedModules = roofInfoArr.some((item: any) => item.ballastPitch !== undefined && item.ballastPitch !== null);

    const dataForRoof = roofInfoArr.map((item) => {
        const hasModules = item.modules > 0;
        // const ballastedModule = sceneViewer?.moduleMeshArr?.find((m: any) => m.userData?.isBallastedRackType && m.roofId === item.roofFaceId);
        return [
            String(item.nameOverride ?? item.roofNum),
            String(Math.round(item.pitchOverride || item.pitch)),
            String(Math.round(item.azimuth) % 360),
            ...(hasBallastedModules ? [
                item.ballastPitch !== undefined && item.ballastPitch !== null ? String(Math.round(item.ballastPitch)) : '',
                item.ballastAzimuth !== undefined && item.ballastAzimuth !== null ? String(Math.round(item.ballastAzimuth) % 360) : ''
            ] : []),
            hasModules ? String(Math.round(item.solarAccess)) : '',
            hasModules ? String(Math.round(Number(item.efficiency))) : '',
            hasModules ? String(item.modules) : '',
            hasModules ? String(arraySizeNumFormat.format(Number(item.arraySize))) : '',
            hasModules ? String(productionNumFormat.format(Number(item.utility_production))) : '',
            hasModules ? String(productionNumFormat.format(Number(item.actual_production))) : ''
        ]
    });

    const {totalArraySize, totalUtilityProduction, totalActualProduction} = roofInfoArr.reduce((p, c) => {
        if (c.modules > 0) {
            p.totalArraySize += Number(c.arraySize);
            p.totalUtilityProduction += Number(c.utility_production);
            p.totalActualProduction += Number(c.actual_production);
        }

        return p;
    }, {
        totalArraySize: 0,
        totalUtilityProduction: 0,
        totalActualProduction: 0,
    });

    let allTreeData : allTreeDataType[] = [];
    if(isHideAllGhostTrees) {
        allTreeData = sceneViewer?.treeGroup.children
            .filter( (data: any) => !data.delStatus) // filter out ghosted trees
            .map( (data: any) => ({
                treeName: data.userData.treeId,
                treeHeight: GetRoundNum(convertQuantityToUnit(data.userData.height, METER, FOOT), 2),
                treeDiameter: GetRoundNum(convertQuantityToUnit(data.userData.crown_radius, METER, FOOT), 2),
        }));
    } else {
        allTreeData = sceneViewer?.treeGroup.children.map( (data:any) => ({
            treeName: data.userData.treeId,
            treeHeight: GetRoundNum(convertQuantityToUnit(data.userData.height, METER, FOOT), 2),
            treeDiameter: GetRoundNum(convertQuantityToUnit(data.userData.crown_radius, METER, FOOT), 2),
        }));
    }

    const secondDataArray = transformDataToObjectArray(allTreeData);
    const roofSolarAccessData = sceneViewer.roofs[0].faces
    const solarDataArray: any[] = [];
    const monthlyProdArr: any[] = [];

    roofSolarAccessData.forEach((item: any) => {
        if (accessoryRoofs.includes(item.roofFaceId)) {
            return;
        }

        const matchingRoof = roofInfoArr.find(roof => roof.roofFaceId === item.roofFaceId);

        //Extract monthly actual production (Jan–Dec)
        if (matchingRoof && matchingRoof.monthlyProductionEstimateData) {
            const monthlyProduction = monthlyEstimateMonths.map(({ long }) => {
            const monthData = matchingRoof.monthlyProductionEstimateData[long];
            return monthData ? Math.round(Number(monthData.actual_production)) : 0;
            });

            if (matchingRoof.modules > 0) {
            monthlyProdArr.push([
                `Roof ${matchingRoof.nameOverride ?? matchingRoof.roofNum}`,
                ...monthlyProduction.map(String)
            ]);
            }
        }

        //Skip roofs where ptHashMonth is not calculated ( no modules on roof )
        if (!item.ptHashMonth) {
            return;
        }

        //Extract ptHashMonth (different dataset)
        const ptHashMonth = item.ptHashMonth;

    

        const ptHashMonthMultiplied = ptHashMonth.map(
            (item: number) => String(Math.round(Number(item) * 100))
        );

        //sync module info into item
        roofInfoArr.forEach((roof) => {
            if (item.roofFaceId === roof.roofFaceId) {
            item.modules = roof.modules;
            return;
            }
        });

        //Original site logic
        if (item.roofId >= 0) {
            roofInfoArr.forEach((roof, i) => {
            if (roof.modules > 0 && item.roofFaceId === roof.roofFaceId) {
                solarDataArray.push(['Roof ' + ( roof.nameOverride ?? (i + 1)), ...ptHashMonthMultiplied]);
                return;
            }
            });
        }
        //Otherwise site is a saved design
        else if (item.modules > 0) {
            solarDataArray.push(['Roof ' + Number(item.nameOverride ?? item.roof_tag_id), ...ptHashMonthMultiplied]);
        }
    });

    const ImageB = homeImage //HomeImage in page 1
    const ImageC = temperatureImage //Temperture like reading image, 3rd image page 1
    const topbar = secondaryHeaderImage //Header Image or 3D view banner
    const area1 = siteReportImages.north //North View
    const area2 = siteReportImages.west //West View
    const area3 = siteReportImages.east //East View
    const north = northBanner // Banner for showing north
    const west = westBanner // Banner for showing west
    const east = eastBanner // Banner for showing east
    const treeTopbar = treeBanner //Header Iage of Tree page
    const topView = siteReportImages.tree //Dummy Image of Top View showing trees

    try {
        const customPageWidth = 210; // Width of the custom page
        const customPageHeight = 297; // Height of the custom page;

        const headerRowHeight = 15; // Height of the header row
        const contentRowHeight = 10; // Height of the content rows
        const address = siteAddress;
        let splitAddress = false;
        if(address.length > 42){
            splitAddress = true;
        }
        const module_wattage = `MODULE WATTAGE USED: ` + wattage + `W`;
        const remoteSiteHeading = 'REMOTE SITE ASSESSMENT RESULTS';
        const roofSummaryHeading = 'RSA - ROOF SUMMARIES';
        const roofSquaresHeading = 'ROOF SQUARES INFORMATION';
        let today = new Date();
        let date = parseInt( String(Number(today.getMonth()) + 1 )) + "-" + today.getDate() + "-" + today.getFullYear();
        let currentDate = 'Date:  ' + date;

        // Function to split text into lines that fit within a given width
        function splitText(text : string, maxWidth : number) {
            const words = text.split(' ');
            let line = '';
            const lines = [];

            for (const word of words) {
                const testLine = line + (line === '' ? '' : ' ') + word;
                const testWidth =
                    pdf.getStringUnitWidth(testLine) *
                    (pdf.getFontSize() / pdf.internal.scaleFactor);

                if (testWidth > maxWidth) {
                    lines.push(line);
                    line = word;
                } else {
                    line = testLine;
                }
            }

            lines.push(line); // Add the last line
            return lines;
        }

        const pdf = new jsPDF({
            unit: 'mm',
            format: [customPageWidth, customPageHeight], // Set custom page size
        });

        //===========================================================================================================================
        //--------------------------------------------Cover Page---------------------------------------------------------------------
        //===========================================================================================================================

        const opportunityName = String(sessionStorage.getItem("opportunityName"))

        // Add Image A to the first page

        const imageAWidth = customPageWidth;
        const imageAHeight = 40; // Adjust the height as needed
        const imageAX = 0; // Adjust the X position as needed
        const imageAY = 0; // Adjust the Y position as needed
        const splitHeaderY = 7;
        const nonSplitHeaderY = 10;
        function addImageHeader(heading : string, headerY : number) {
            pdf.addImage(headerSiteImage, 'PNG', imageAX, imageAY, imageAWidth, imageAHeight, 'headerSiteImage', 'FAST');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(10);
            pdf.text(heading, 100, headerY, {align: 'center'})
        }

        if(splitAddress){ //If address has more than 42 characters, split address into two lines
            addImageHeader(remoteSiteHeading, splitHeaderY);
            pdf.text(opportunityName, 100, splitHeaderY + 6, {align: 'center'});
            pdf.text(splitText(address, 75), 100, splitHeaderY + 12, {align: 'center'});
            pdf.text(currentDate, 100, nonSplitHeaderY + 18.5, {align: 'center'});
            pdf.text(module_wattage, 100, nonSplitHeaderY + 24.5, {align: 'center'});
        }
        else{
            addImageHeader(remoteSiteHeading, nonSplitHeaderY);
            pdf.text(opportunityName, 100, nonSplitHeaderY + 6, {align: 'center'});
            pdf.text(splitText(address, 75), 100, nonSplitHeaderY + 12,{align: 'center'});
            pdf.text(currentDate, 100, nonSplitHeaderY + 18.5, {align: 'center'});
            pdf.text(module_wattage, 100, nonSplitHeaderY + 24.5, {align: 'center'});
        }
        // Add Image B to the first page
        const imageBWidth = customPageWidth;
        const imageBHeight = 230; // Adjust the height as needed
        const imageBX = 0; // Adjust the X position as needed
        const imageBY = imageAY + imageAHeight; // Position below Image A

        // const homeImageDimensions = await getOptimalDimensions(ImageB, imageBWidth, imageBHeight);
        //
        // //center the home image
        // const homeImageWidth = homeImageDimensions.optimalWidth;
        // const homeImageHeight = homeImageDimensions.optimalHeight;

        // const homeImageX = (imageBWidth - homeImageWidth) / 2 + imageBX;
        // const homeImageY = (imageBHeight - homeImageHeight) / 2 + imageBY;

        pdf.addImage(ImageB, 'PNG', imageBX, imageBY, imageBWidth, imageBHeight, 'homeImage', 'FAST');

        // Add Image C to the first page
        const imageCWidth = customPageWidth;
        const imageCHeight = 25; // Adjust the height as needed
        const imageCX = 0; // Adjust the X position as needed
        const imageCY = imageBY + imageBHeight; // Position below Image B
        pdf.addImage(ImageC, 'PNG', imageCX, imageCY, imageCWidth, imageCHeight, 'imageC', 'FAST');

        //===========================================================================================================================
        //------------------------------------------------Roof Summaries-------------------------------------------------------------
        //===========================================================================================================================

        // Define table column headers
        let tableHeaders = ['ROOF', 'PITCH', 'AZIMUTH', 'SOLAR ACCESS', 'EFFICIENCY', 'MODULES', 'ARRAY SIZE', 'UTILITY PRODUCTION', 'ACTUAL PRODUCTION'];
        let tHeaders = ['#', '(DEGREES)', '(DEGREES)', '(UNSHADED %)', '(TSRF%)', `${wattage} W`, '(kW DC)', '(kWh)', '(kWh)'];


        if (hasBallastedModules) {
            tableHeaders.splice(3, 0, 'RACKING PITCH', 'RACKING AZIMUTH');
            tHeaders.splice(3, 0, '(DEGREES)', '(DEGREES)');
        }

        const shift = hasBallastedModules ? 2 : 0;

        // Define the row height and column width
        let colWidth = customPageWidth / tableHeaders.length;

        // Define the initial Y position for the table
        let tableY = 40.5; // Adjust the Y position as needed
        const maxRowsPerDataPage = (Math.floor((customPageHeight - tableY - 20) / 10));
        pdf.setFillColor(91, 138, 190);

        // Define the border color for headers
        pdf.setDrawColor(255, 255, 255); // White color for the border


        // Initialize variables for the third section
        let currentDataRow = 0;
        let currentDataY = addNewPageForThirdSection();

        // Function to add a new page for the third section
        function addNewPageForThirdSection() {
            pdf.addPage();

            // Add Image A to the page (you can also add different images)
            addImageHeader(roofSummaryHeading, nonSplitHeaderY);
            pdf.text(opportunityName, 100, 20, {align: 'center'});
            if(splitAddress){
                pdf.text(splitText(address, 75), 100, 25, {align: 'center'});
            }
            else{
               pdf.text(address, 100, 25, {align: 'center'});
            }
            if(selectedLossesVendor !== 'oneDRAW') pdf.text('Vendor: ' + selectedLossesVendor, 100, 35,{align: 'center'});

            pdf.setFillColor(91, 138, 190);

            // Define the border color for headers
            pdf.setDrawColor(255, 255, 255); // White color for the border

            // Add the table headers (first row) with blue background
            for (let i = 0; i < tableHeaders.length; i++) {
                let headerY = tableY - 1 + headerRowHeight / 2;
                pdf.setFillColor(91, 138, 190); // blue color for the background
                pdf.rect(i * colWidth, tableY, colWidth, headerRowHeight, 'FD'); // Fill header background and draw border
                pdf.setTextColor(0); // Set text color to black
                pdf.setFontSize(7);
                pdf.setFont("", 'bold');
                const textHeader = splitText(tableHeaders[i], 15);
                textHeader.forEach((line) => {
                    pdf.text(line, i * colWidth + colWidth / 2, headerY, {align: 'center'});
                    headerY += 2.5;
                });
                pdf.text(tHeaders[i], i * colWidth + colWidth / 2, headerY, {align: 'center'});
            }

            // Reset the text and background + border color after drawing headers
            pdf.setFillColor(255, 255, 255); // Set to white for content rows
            pdf.setDrawColor(0); // Reset border color
            pdf.setFont("", 'normal');

            // Return the Y-coordinate for the content rows
            return tableY + headerRowHeight - 15; // Adjust the offset as needed
        }

        //Ensure roofs are sorted by roof num stored in first index of dataForRoof (low -> high)
        dataForRoof.sort((a, b) => Number(a[0]) - Number(b[0]));

        // Loop through dataArray for the third section
        for (let rowIndex = 0; rowIndex < dataForRoof.length; rowIndex++) {
            const rowData = dataForRoof[rowIndex];
            // Set the font size for rows
            pdf.setFontSize(10);
            // Calculate the Y position for the current row
            const currentRowY = currentDataY + headerRowHeight + currentDataRow * contentRowHeight;

            // Set the border color for rows to white
            pdf.setDrawColor(255, 255, 255);

            // Alternate row background colors (light grey)
            if (currentDataRow % 2 === 0) {
                pdf.setFillColor(179, 178, 177); // Light grey
            } else {
                pdf.setFillColor(202, 200, 200); // White
            }

            // Add row background color
            pdf.rect(0, currentRowY, customPageWidth, contentRowHeight, 'FD');

            // Set the border for the current row
            pdf.rect(0, currentRowY, customPageWidth, contentRowHeight);

            for (let colIndex = 0; colIndex < tableHeaders.length; colIndex++) {
                const cellData = rowData[colIndex];

                // Calculate the X position for the current cell
                const currentCellX = colIndex * colWidth;
                const textY = currentRowY + contentRowHeight / 2;

                // Add cell data (dummy text)
                pdf.text(cellData, currentCellX + colWidth / 2, textY + 1, {align: 'center'});

                // Add white borders to the cell
                pdf.rect(currentCellX, currentRowY, colWidth, contentRowHeight);
            }

            currentDataRow++; // Increment the current row count

            // Check if there's enough space for the next row on the current page
            if (currentDataRow >= maxRowsPerDataPage) {
                currentDataY = addNewPageForThirdSection();
                currentDataRow = 0; // Reset the current row count on the new page
            }
        }

        // After dataArray is completed, add the last rows
        // Add a new row for total and average header
        const newRowY = currentDataY + headerRowHeight + currentDataRow * contentRowHeight;
        const newRowHeight = 8; // Adjust the height as needed
        pdf.setFillColor(91, 138, 190); // blue background
        pdf.rect((3 + shift) * colWidth, newRowY, 2 * colWidth, newRowHeight, 'FD');
        pdf.rect((5 + shift) * colWidth, newRowY, 4 * colWidth, newRowHeight, 'FD');
        pdf.setTextColor(0); // Set text color to black
        pdf.setFontSize(7);
        pdf.setFont("", 'bold');
        pdf.text('WEIGHTED AVERAGES', (4 + shift) * colWidth, newRowY + 1 + newRowHeight / 2, {align: 'center'});
        pdf.text('TOTALS', (7 + shift) * colWidth, newRowY + 1 + newRowHeight / 2, {align: 'center'});

        // Add a new row for total and average values
        const newRow2Y = newRowY + newRowHeight;
        const newRow2Height = 10; // Adjust the height as needed
        pdf.setFillColor(100, 101, 105); // background
        pdf.rect((3 + shift) * colWidth, newRow2Y, 2 * colWidth, newRow2Height, 'FD');
        pdf.rect((5 + shift) * colWidth, newRow2Y, 4 * colWidth, newRow2Height, 'FD');
        pdf.setTextColor(255, 255, 255); // Set text color to white
        pdf.setFontSize(12);
        pdf.setFont("", 'normal');

        // Add content to each of the six columns
        const newRowX = 3 * colWidth;

        // Add text to the first three columns
        const y = newRow2Y + 1 + newRow2Height / 2;
        pdf.text(String(modeCircleSolar), newRowX + colWidth / 2, y, {align: 'center'});
        if(efficiencyOverride) { //If efficiency was overridden during finalization use that value + update UI to reflect it
            pdf.setFontSize(6);
            pdf.text("Overridden",  newRowX + 1 * colWidth + colWidth / 2, y - 3, {align: 'center'});
            pdf.setFontSize(12);
            pdf.text(String(efficiencyOverride), newRowX + 1 * colWidth + colWidth / 2, y + 1.5, {align: 'center'});
        } else { //Display oneDRAW calculated efficiency otherwise
            pdf.text(String(efficiency), newRowX + 1 * colWidth + colWidth / 2, y, {align: 'center'});
        }
        pdf.text(String(modeCount), newRowX + 2 * colWidth + colWidth / 2, y, {align: 'center'});
        pdf.text(String(arraySizeNumFormat.format(GetRoundNum(totalArraySize, 3))), newRowX + 3 * colWidth + colWidth / 2, y, {align: 'center'});
        pdf.text(String(productionNumFormat.format(Math.round(totalUtilityProduction))), newRowX + 4 * colWidth + colWidth / 2, y, {align: 'center'});
        pdf.text(String(productionNumFormat.format(Math.round(totalActualProduction))), newRowX + 5 * colWidth + colWidth / 2, y, {align: 'center'});


        //===========================================================================================================================
        //----------------------------------------------3D Views---------------------------------------------------------------------
        //===========================================================================================================================

        pdf.addPage();
        // Define the dimensions of the topbar image

        const topbarHeight = 12;
        // Add topbar for page 3 to the second page (you can also add different images)

        // Calculate the remaining height for the three images
        const remainingHeight = customPageHeight - topbarHeight;

        // Define the dimensions of the three images (assuming they have the same width and height)
        const imageWidth = customPageWidth;
        const imageHeight = remainingHeight / 3; // Divide the remaining height into three equal parts

        // Calculate the Y coordinates for the three images
        const image1Y = topbarHeight; // Start below the topbar
        const image2Y = image1Y + imageHeight; // Below the first image
        const image3Y = image2Y + imageHeight; // Below the second image

        // Add the topbar image to the top of the page
        pdf.addImage(topbar, 'PNG', 0, 0, customPageWidth + 1, topbarHeight + .1, 'topbar', 'FAST');

        // Add the three images one under another
        pdf.addImage(area1, 'PNG', 0, image1Y, imageWidth, imageHeight, 'area1', 'FAST');
        pdf.addImage(north, 'PNG', 0, image1Y + 89, imageWidth, 6, 'north', 'FAST');
        pdf.addImage(area2, 'PNG', 0, image2Y, imageWidth, imageHeight, 'area2', 'FAST');
        pdf.addImage(west, 'PNG', 0, image2Y + 89, imageWidth, 6.5, 'west', 'FAST');
        pdf.addImage(area3, 'PNG', 0, image3Y, imageWidth, imageHeight, 'area3', 'FAST');
        pdf.addImage(east, 'PNG', 0, image3Y + 89, imageWidth, 6, 'east', 'FAST');

        //===========================================================================================================================
        //--------------------------------------------- Tree References -------------------------------------------------------------
        //===========================================================================================================================

        // Create a new page
        pdf.addPage();

        pdf.addImage(treeTopbar, 'PNG', 0, 0, customPageWidth + 1, topbarHeight + .1, 'treeTopbar', 'FAST');
        pdf.addImage(topView, 'PNG', 0, image1Y + 1.25, imageWidth, imageHeight + 65, 'topView', 'FAST');
        pdf.setFillColor(179, 178, 177);
        pdf.rect(5, 160, customPageWidth - 10, 6, 'F');
        pdf.setTextColor(0); // Set text color to black
        pdf.setFontSize(8);
        pdf.text('TREE REFERENCE', 100, 164, {align: 'center'});
        pdf.setTextColor(0);
        pdf.setFillColor(255, 255, 255); // White

        // Calculate the total width of the 12 columns
        const totalColumnWidth = customPageWidth - 10; // Subtracting 10 to match the width of the existing rectangle
        const columnCount = 12;
        const columnWidthh = totalColumnWidth / columnCount;
        const columnsPerGroup = 3; // Number of columns in each group
        const rowHeight = 6;

        // Calculate the Y position for the row (under the existing rectangle)
        const rowY = 166; // Adjust this value to leave some space below the existing rectangle
        const tableTreeHeaders = ['#', 'HT.', 'DIA.', '#', 'HT.', 'DIA.', '#', 'HT.', 'DIA.', '#', 'HT.', 'DIA.'];
        const maxRowsPerPage = (Math.floor((customPageHeight - rowY) / rowHeight)) - 1;
        const treeHeadingY = 166

        for (let colIndex = 0; colIndex < tableTreeHeaders.length; colIndex++) {
            const cellX = colIndex * columnWidthh + 5; // Calculate the X position for each column
            pdf.setFillColor(255, 255, 255); // White
            pdf.rect(cellX, treeHeadingY, columnWidthh, 6, 'F'); // Create each column with some height
            pdf.text(tableTreeHeaders[colIndex], cellX + columnWidthh / 2, treeHeadingY + 4, {align: 'center'});
        }

        // Function to add a new page and display tableTreeHeaders
        function addNewPageWithHeaders() {
            pdf.addPage();
            // Calculate the Y position for the row on the new page
            const newRowY = 10; // You can adjust this value based on your layout
            pdf.setTextColor(0);
            pdf.setFontSize(8);
            pdf.setFillColor(179, 178, 177);
            pdf.rect(5, 5, customPageWidth - 10, 6, 'F');
            pdf.setTextColor(0); // Set text color to black
            pdf.setFontSize(8);
            pdf.text('TREE REFERENCE', 100, 9, {align: 'center'});
            pdf.setTextColor(0);
            pdf.setFillColor(255, 255, 255); // White

            // Create a row with 12 columns for tableTreeHeaders
            for (let colIndex = 0; colIndex < tableTreeHeaders.length; colIndex++) {
                const cellX = colIndex * columnWidthh + 5; // Calculate the X position for each column
                pdf.setFillColor(255, 255, 255); // White
                pdf.rect(cellX, newRowY, columnWidthh + 1, 6, 'F'); // Create each column with some height
                pdf.text(tableTreeHeaders[colIndex], cellX + columnWidthh / 2, newRowY + 4, {align: 'center'});
            }
        }

        // Initialize variables to keep track of rows printed on the current page and Y-coordinate offset
        let currentRow = 0;
        let yOffset = 0; // Initialize the Y-coordinate offset

        //Round all values in secondDataArray
        function roundNumbers(arr : number[] | string[]){
            return arr.map(item => {
                if (typeof item === 'string' && item.includes("'")) {
                    // Remove the trailing apostrophe and convert to number
                    let num = parseFloat(item.replace("'", ""));
                    // Check if item is a number and round it
                    return isNaN(num) ? item : Math.round(num) + "'";
                }
                return item;
            });
        }
        let roundedDataArray = secondDataArray.map(arr => roundNumbers(arr));

        // Add the table rows
        for (let rowIndex = 0; rowIndex < roundedDataArray.length; rowIndex++) {
            const rowData = roundedDataArray[rowIndex];

            // Check if there's enough space for the next row on the current page
            if (currentRow >= maxRowsPerPage) {
                addNewPageWithHeaders();
                currentRow = 0; // Reset the current row count on the new page
                yOffset = -156; // Reset the Y-coordinate offset
            }
            // Calculate the Y position for the current row
            const currentRowY = rowY + rowHeight + currentRow * rowHeight + yOffset;

            // Alternate row background colors (light grey and white)
            if (currentRow % 2 === 0) {
                pdf.setFillColor(179, 178, 177); // Light grey
            } else {
                pdf.setFillColor(202, 200, 200); // White
            }

            // Add row background color
            pdf.rect(5, currentRowY, totalColumnWidth, rowHeight, 'F');

            // Set the border for the right side of each cell (after every 3 columns)
            for (let colIndex = 0; colIndex < tableTreeHeaders.length; colIndex++) {
                if ((colIndex + 1) % columnsPerGroup === 0 && colIndex !== columnCount - 1) {
                    const borderX = (colIndex + 1) * columnWidthh + 5;
                    pdf.setDrawColor(255, 255, 255); // White border color
                    pdf.rect(borderX, currentRowY, 0.1, rowHeight); // Add the border
                }
            }

            for (let colIndex = 0; colIndex < tableTreeHeaders.length; colIndex++) {
                const cellData = rowData[colIndex];

                // Calculate the X position for the current cell
                const currentCellX = colIndex * columnWidthh + 5;
                const textY = currentRowY + rowHeight / 2;

                // Add cell data (dummy text)
                pdf.text(String(cellData), currentCellX + columnWidthh / 2, textY + 1, {align: 'center'});
            }

            currentRow++; // Increment the current row count

            // If the currentRow reaches maxRowsPerPage, increase the yOffset
            if (currentRow >= maxRowsPerPage) {
                yOffset += 5; // Adjust this value as needed
            }
        }

        // Add a new page if there's more data in secondDataArray
        if (currentRow >= maxRowsPerPage) {
            addNewPageWithHeaders();
        }

        //===========================================================================================================================
        //--------------------------------------Monthly Solar Access Values----------------------------------------------------------
        //===========================================================================================================================

        // Create a new page
        // pdf.addPage();
        // pdf.setFillColor(179, 178, 177);
        // pdf.rect(5, 5, customPageWidth-10, 6, 'F'); // filled square
        // pdf.setTextColor(0); // Set text color to black
        // pdf.setFontSize(8);
        // pdf.text('MONTHLY SOLAR ACCESS VALUES', 100, 9, 'center');
        const columnSolarCount = 13;
        const solarrowY = 11; // Adjust this value to leave some space below the existing rectangle
        const columnSolarWidthh = totalColumnWidth / columnSolarCount;
        const tableSolarHeaders = ['ROOF', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const maxRowsPerSolarPage = (Math.floor((customPageHeight - solarrowY) / rowHeight)) - 1;


        // Define a function to add a new page for solar data
        function addNewPageForSolarData() {
            pdf.addPage();
            pdf.setFillColor(179, 178, 177);
            pdf.rect(5, 5, customPageWidth - 10, 6, 'F');
            pdf.setTextColor(0);
            pdf.setFontSize(8);
            pdf.text('MONTHLY SOLAR ACCESS VALUES', 100, 9, {align: 'center'});

            // Calculate the Y position for the header row
            const headerRowY = 11; // Adjust as needed
            const columnSolarCount = 13;
            const columnSolarWidthh = totalColumnWidth / columnSolarCount;

            // Create a row with 13 columns for the solar headers
            for (let colIndex = 0; colIndex < columnSolarCount; colIndex++) {
                const cellX = colIndex * columnSolarWidthh + 5;
                pdf.setFillColor(255, 255, 255); // White
                pdf.setTextColor(0); // Set text color to black

                // Add the text in each column
                const textX = cellX + columnSolarWidthh / 2;
                const textY = headerRowY + 4;

                pdf.text(tableSolarHeaders[colIndex], textX, textY, {align: 'center'});
            }

            // Calculate the height for each row (same as solarrowHeight)
            const solarrowHeight = 6;

            // Return the Y-coordinate for the content rows
            return headerRowY + solarrowHeight + 5; // Adjust the offset as needed
        }

        // Initialize variables for solar data
        let currentSolarRow = 0;
        let currentSolarY = addNewPageForSolarData();

        // Add the table rows for solar data
        for (let rowIndex = 0; rowIndex < solarDataArray.length; rowIndex++) {
            const rowData = solarDataArray[rowIndex];
            const solarrowHeight = 6;

            // Calculate the Y position for the current row
            const currentRowY = currentSolarY + currentSolarRow * solarrowHeight;

            // Alternate row background colors (light grey and white)
            if (currentSolarRow % 2 === 0) {
                pdf.setFillColor(179, 178, 177); // Light grey
            } else {
                pdf.setFillColor(202, 200, 200); // White
            }

            // Add row background color
            pdf.rect(5, currentRowY - 5, totalColumnWidth, solarrowHeight, 'F');

            for (let colIndex = 0; colIndex < columnSolarCount; colIndex++) {
                const cellData = rowData[colIndex];

                // Calculate the X position for the current cell
                const cellX = colIndex * columnSolarWidthh + 5;
                const textY = currentRowY - 5 + solarrowHeight / 2;

                // Add cell data (dummy text)
                pdf.text(cellData, cellX + columnSolarWidthh / 2, textY + 1, {align: 'center'});
            }

            currentSolarRow++; // Increment the current row count

            // Check if there's enough space for the next row on the current page
            if (currentSolarRow >= maxRowsPerSolarPage) {
                currentSolarY = addNewPageForSolarData();
                currentSolarRow = 0; // Reset the current row count on the new page
            }
        }

        //===========================================================================================================================
        //--------------------------------------Monthly Estimated Production------------------------------------------------------------
        //===========================================================================================================================

        function addNewPageForProductionData() {
            pdf.addPage();
            pdf.setFillColor(179, 178, 177);
            pdf.rect(5, 5, customPageWidth - 10, 6, 'F');
            pdf.setTextColor(0);
            pdf.setFontSize(8);
            pdf.text('MONTHLY ESTIMATED PRODUCTION', 100, 9, {align: 'center'});

            const headerRowY = 11;
            const columnProdCount = 13;
            const columnProdWidth = totalColumnWidth / columnProdCount;
            const tableProdHeaders = ['ROOF', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                                    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

            // Draw header row
            for (let colIndex = 0; colIndex < columnProdCount; colIndex++) {
                const cellX = colIndex * columnProdWidth + 5;
                pdf.setFillColor(255, 255, 255);
                pdf.setTextColor(0);

                const textX = cellX + columnProdWidth / 2;
                const textY = headerRowY + 4;
                pdf.text(tableProdHeaders[colIndex], textX, textY, {align: 'center'});
            }

            const prodRowHeight = 6;
            return headerRowY + prodRowHeight + 5;
        }

        // Determine where to start the production table
        const prodRowHeight = 6;
        const requiredProdHeight = (monthlyProdArr.length + 2) * prodRowHeight + 10; // +1 for header, +1 for summary
        const spaceRemaining = customPageHeight - (currentSolarY + currentSolarRow * rowHeight);

        let currentProdY;
        if (requiredProdHeight <= spaceRemaining) {
            // Same page, below solar table
            currentProdY = currentSolarY + currentSolarRow * rowHeight + 10;
            pdf.setFillColor(179, 178, 177);
            pdf.rect(5, currentProdY - 6, customPageWidth - 10, 6, 'F');
            pdf.setTextColor(0);
            pdf.setFontSize(8);
            pdf.text('MONTHLY ESTIMATED PRODUCTION', 100, currentProdY - 2, {align: 'center'});

            const columnProdCount = 13;
            const columnProdWidth = totalColumnWidth / columnProdCount;
            const tableProdHeaders = ['ROOF', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                                    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

            for (let colIndex = 0; colIndex < columnProdCount; colIndex++) {
                const cellX = colIndex * columnProdWidth + 5;
                pdf.setFillColor(255, 255, 255);
                pdf.setTextColor(0);
                pdf.text(tableProdHeaders[colIndex], cellX + columnProdWidth / 2, currentProdY + 4, {align: 'center'});
            }

            currentProdY += prodRowHeight + 5;
        } else {
            // Not enough space → new page
            currentProdY = addNewPageForProductionData();
        }

        // --- Calculate monthly totals across all roofs ---
        const totals = Array(12).fill(0);
        for (let rowIndex = 0; rowIndex < monthlyProdArr.length; rowIndex++) {
            const row = monthlyProdArr[rowIndex];
            for (let month = 1; month <= 12; month++) {
                totals[month - 1] += Number(row[month]) || 0;
            }
        }

        // Draw production rows
        let currentProdRow = 0;
        const maxRowsPerProdPage = (Math.floor((customPageHeight - 11) / rowHeight)) - 1;

        for (let rowIndex = 0; rowIndex < monthlyProdArr.length; rowIndex++) {
            const rowData = monthlyProdArr[rowIndex];
            const currentRowY = currentProdY + currentProdRow * prodRowHeight;

            // Alternate row background
            if (currentProdRow % 2 === 0) {
                pdf.setFillColor(179, 178, 177);
            } else {
                pdf.setFillColor(202, 200, 200);
            }
            pdf.rect(5, currentRowY - 5, totalColumnWidth, prodRowHeight, 'F');

            // Cell data
            for (let colIndex = 0; colIndex < rowData.length; colIndex++) {
                const cellData = rowData[colIndex];
                const cellX = colIndex * (totalColumnWidth / 13) + 5;
                const textY = currentRowY - 5 + prodRowHeight / 2;
                pdf.text(String(cellData), cellX + (totalColumnWidth / 13) / 2, textY + 1, {align: 'center'});
            }

            currentProdRow++;

            if (currentProdRow >= maxRowsPerProdPage) {
                currentProdY = addNewPageForProductionData();
                currentProdRow = 0;
            }
        }

        // --- Draw summary row at very end ---
        if (currentProdRow >= maxRowsPerProdPage) {
            currentProdY = addNewPageForProductionData();
            currentProdRow = 0;
        }

        const summaryRowY = currentProdY + currentProdRow * prodRowHeight;

        // Grey background for total row
        pdf.setFillColor(160, 160, 160);
        pdf.rect(5, summaryRowY - 5, totalColumnWidth, prodRowHeight, 'F');

        // "TOTAL" label
        pdf.setFont("", "bold");
        pdf.text("TOTAL", 5 + (totalColumnWidth / 13) / 2, summaryRowY - 5 + prodRowHeight / 2 + 1, {align: "center"});

        // Month totals
        for (let month = 0; month < 12; month++) {
            const cellX = (month + 1) * (totalColumnWidth / 13) + 5;
            const textY = summaryRowY - 5 + prodRowHeight / 2;
            pdf.text(
                String(totals[month].toFixed(0)),
                cellX + (totalColumnWidth / 13) / 2,
                textY + 1,
                {align: "center"}
            );
        }

        // --- Draw grand total row (sum of all totals) ---
        currentProdRow++;
        if (currentProdRow >= maxRowsPerProdPage) {
            currentProdY = addNewPageForProductionData();
            currentProdRow = 0;
        }

        const grandTotalRowY = currentProdY + currentProdRow * prodRowHeight;
        const grandTotalValue = totals.reduce((sum, val) => sum + val, 0);

        // Highlight row
        pdf.setFillColor(120, 120, 120);
        pdf.rect(5, grandTotalRowY - 5, totalColumnWidth, prodRowHeight, 'F');

        // "GRAND TOTAL" label
        pdf.text(
            "TOTAL SYSTEM PRODUCTION (ROUNDED)",
            29.5 + (totalColumnWidth / 13) / 2,
            grandTotalRowY - 5 + prodRowHeight / 2 + 1,
            {align: "center"}
        );

        // Value centered across all month columns
        pdf.text(
            String(grandTotalValue.toFixed(0)),
            5 + totalColumnWidth / 2,
            grandTotalRowY - 5 + prodRowHeight / 2 + 1,
            {align: "center"}
        );

        pdf.setFont("", "normal"); // reset font


        //===========================================================================================================================
        //----------------------------------------------Roof Squares Table-----------------------------------------------------------
        //===========================================================================================================================

        //Only create roof squares page/table if roof square data exists
        if (Array.isArray(viewerState.roofSquareTableData) || roofSquareTableData.length > 0) {
            // Define table column headers

            const subHeaders = ['BUILDING', 'ROOF', 'LOW SLOPE', 'NORMAL SLOPE', 'TOTAL SQUARES'];
            const roofSquareData :  BuildingRoofSquares[] = viewerState.roofSquareTableData
                .map((x : any) => x.data)
                .flat()
                .filter((y) => y.isActive)
                .sort((a, b) => a.buildingId - b.buildingId); //Ensure data is sorted by building (low -> high)
            if(roofSquareData.length === 0) return pdf;
            const roofSquarePricingData : any = viewerState.roofSquarePricingData;
            let newRow3Y = 40.5;

            //Instantiate roof square data arrays and other variables
            let roofSquareArr = [];
            currentDataRow = 0;
            let currentRowY = 0;
            let newRoofSquareData = JSON.parse(JSON.stringify(roofSquareData));
            let currentBuildingID = newRoofSquareData[0].buildingId; //Set to first buildingID
            let newRow4Y;
            const newRow4Height = 10; // Adjust the height as needed
            let buildingCount = 1;

            //Instantiate building total variables
            let lowSlopeTotalRounded = 0;
            let normalSlopeTotalRounded = 0;

            const formatBuildingTotal = (value: number) =>
                useJune2026SquareCalculation ? Number(value).toFixed(2) : String(value);

            const formatSiteSummaryRounded = (value: number, isTotalRow: boolean) => {
                if (useJune2026SquareCalculation && !isTotalRow) {
                    return (!value || value === 0) ? '0' : Number(value).toFixed(0);
                }
                return String(value);
            };

            addNewPageForRoofSquares();

            function addNewPageForRoofSquares() {
                newRow3Y = 40.5;
                pdf.setFillColor(91, 138, 190);

                // Define the border color for headers
                pdf.setDrawColor(255, 255, 255); // White color for the border

                pdf.addPage();

                // Add Image A to the page (you can also add different images)
                addImageHeader(roofSquaresHeading, nonSplitHeaderY);
                pdf.text(opportunityName, 100, 16, {align: 'center'});
                if(splitAddress){
                    pdf.text(splitText(address, 75), 100, 22, {align: 'center'});
                }
                else{
                   pdf.text(address, 100, 22, {align: 'center'});
                }

                addRoofSquareTableHeaders(newRow3Y);
            }

            // Loop through dataArray for the third section
            for (let rowIndex = 0; rowIndex < newRoofSquareData.length; rowIndex++) {
                //Transfer data from roof square objects (slope + pricing) to array
                roofSquareArr[0] = buildingCount;
                if (currentBuildingID !== newRoofSquareData[rowIndex].buildingId) {
                    addBuildingRoundedTotalRow(currentRowY);
                    lowSlopeTotalRounded = 0;
                    normalSlopeTotalRounded = 0;
                    buildingCount++;
                    currentBuildingID = newRoofSquareData[rowIndex].buildingId;
                    if (newRow3Y + 45 > customPageHeight - 10) {
                        addNewPageForRoofSquares();
                    } else {
                        if(currentDataRow >= maxRowsPerDataPage - 8){ //Check if there is enough space for headers + first row
                             addNewPageForRoofSquares();
                            currentDataRow = 0; // Reset the current row count on the new page
                        } else {
                            newRow3Y += 40;
                            addRoofSquareTableHeaders(currentRowY + (contentRowHeight * 2));
                        }
                    }
                }
                roofSquareArr[1] = newRoofSquareData[rowIndex].nameOverride || newRoofSquareData[rowIndex].roofId;
                roofSquareArr[2] = Number(newRoofSquareData[rowIndex].lowSlopeSquare);
                roofSquareArr[3] = Number(newRoofSquareData[rowIndex].normalSlopeSquare);
                lowSlopeTotalRounded += roofSquareArr[2];
                normalSlopeTotalRounded += roofSquareArr[3];

                // Set the font size for rows
                pdf.setFontSize(10);
                // Calculate the Y position for the current row
                currentRowY = newRow3Y + (headerRowHeight * 2) + currentDataRow * contentRowHeight;
                pdf.rect(0, currentRowY, customPageWidth, contentRowHeight, 'FD');

                // Set the border color for rows to white
                pdf.setDrawColor(255, 255, 255);

                // Alternate row background colors (light grey)
                if (currentDataRow % 2 === 0) {
                    pdf.setFillColor(179, 178, 177); // Light grey
                } else {
                    pdf.setFillColor(202, 200, 200); // White
                }

                // Add row background color
                pdf.rect(0, currentRowY, customPageWidth, contentRowHeight, 'FD');

                // Set the border for the current row
                pdf.rect(0, currentRowY, customPageWidth, contentRowHeight);

                for (let colIndex = 0; colIndex < roofSquareArr.length; colIndex++) {
                    const cellData = roofSquareArr[colIndex];

                    // Calculate the X position for the current cell
                    const currentCellX = colIndex * colWidth;
                    const textY = currentRowY + contentRowHeight / 2;



                    // Add cell data (dummy text)
                    const num = Number(cellData);
                    let displayValue: string;
                    if (colIndex >= 2 && useJune2026SquareCalculation) {
                        displayValue = (!num || num === 0) ? '0' : num.toFixed(2);
                    } else {
                        displayValue = num % 1 === 0 ? String(num) : String(num.toFixed(2));
                    }

                    pdf.text(displayValue, currentCellX + colWidth / 2, textY + 1, { align: 'center' });

                    // Add white borders to the cell
                    pdf.rect(currentCellX, currentRowY, colWidth, contentRowHeight);
                }

                currentDataRow++; // Increment the current row count

                // Check if there's enough space for the next row on the current page
                if (currentDataRow >= maxRowsPerDataPage - 1) {
                    addNewPageForRoofSquares();
                    currentDataRow = 0; // Reset the current row count on the new page
                }
            }
            addBuildingRoundedTotalRow(currentRowY)

            function addBuildingRoundedTotalRow(currentRowY : number) {
                // Check if there's enough space for the next row on the current page
                if (currentDataRow >= maxRowsPerDataPage) {
                    addNewPageForRoofSquares();
                    currentDataRow = 0; // Reset the current row count on the new page
                }
                // Add a new row for building rounded total
                newRow4Y = currentRowY + 10;
                pdf.setFillColor(100, 101, 105); // background
                pdf.rect(0, newRow4Y, 2 * colWidth, newRow4Height, 'FD');
                pdf.rect(2 * colWidth, newRow4Y, 3 * colWidth, newRow4Height, 'FD');
                //Add grid-like pattern to building totals row
                pdf.rect(2 * colWidth, newRow4Y, colWidth, contentRowHeight);
                pdf.rect(3 * colWidth, newRow4Y, colWidth, contentRowHeight);
                pdf.rect(4 * colWidth, newRow4Y, colWidth, contentRowHeight);

                pdf.setTextColor(255, 255, 255); // Set text color to white
                pdf.setFontSize(8);

                //Add text for building rounded total
                pdf.text('BUILDING ' + buildingCount + ' TOTALS', colWidth, newRow4Y + 6, {align: 'center'});
                pdf.setFontSize(10);
                const buildingLowTotal = useJune2026SquareCalculation
                    ? lowSlopeTotalRounded
                    : roundedBasedOn33Percent(lowSlopeTotalRounded);
                const buildingNormalTotal = useJune2026SquareCalculation
                    ? normalSlopeTotalRounded
                    : roundedBasedOn33Percent(normalSlopeTotalRounded);
                const buildingGrandTotal = useJune2026SquareCalculation
                    ? lowSlopeTotalRounded + normalSlopeTotalRounded
                    : buildingLowTotal + buildingNormalTotal;
                pdf.text(formatBuildingTotal(buildingLowTotal), 2.5 * colWidth, newRow4Y + 6, {align: 'center'});
                pdf.text(formatBuildingTotal(buildingNormalTotal), 3.5 * colWidth, newRow4Y + 6, {align: 'center'});
                pdf.text(formatBuildingTotal(buildingGrandTotal), 4.5 * colWidth, newRow4Y + 6, {align: 'center'});
            }

            function addRoofSquareTableHeaders(yVal : number) {
                //Add Squares Per Roof and Roof Summary headers
                pdf.setFillColor(91, 138, 190); // blue background
                pdf.rect(0, yVal, customPageWidth, headerRowHeight, 'FD'); //Squares Per Roof rectangle
                pdf.setTextColor(0); // Set text color to black
                pdf.setFont("", 'bold');
                pdf.text('SQUARES PER ROOF', customPageWidth / 2, yVal + 3 + headerRowHeight / 2, {align: 'center'});
                pdf.text('BUILDING ' + buildingCount, customPageWidth / 2, yVal - 1 + headerRowHeight / 2, {align: 'center'});

                colWidth = customPageWidth / subHeaders.length;

                //Add sub-headers with blue background
                for (let i = 0; i < subHeaders.length; i++) {
                    let headerY = yVal + headerRowHeight;
                    pdf.setFillColor(91, 138, 190); // blue background
                    pdf.rect(i * colWidth, headerY, colWidth, headerRowHeight, 'FD'); // Fill header background and draw border
                    pdf.setFontSize(7);
                    const textHeader = splitText(subHeaders[i], 15);
                    textHeader.forEach((line) => {
                        pdf.text(line, (i * colWidth + colWidth / 2), headerY + 7, {align: 'center'});
                        headerY += 2.5;
                    });
                }

                // Reset the background color and border color after drawing headers
                pdf.setFillColor(255, 255, 255); // Set to white for content rows
                pdf.setDrawColor(0); // Reset border color
                pdf.setFont("", 'normal');
            }

            //===========================================================================================================================
            //----------------------------------------------Total Site Summary Table-----------------------------------------------------------
            //===========================================================================================================================

            //Set up new page for total site summary table
            pdf.addPage();
            addImageHeader(roofSquaresHeading, nonSplitHeaderY);
            pdf.text(opportunityName, 100, 16, {align: 'center'});
            if(splitAddress){
                pdf.text(splitText(address, 75), 100, 22, {align: 'center'});
            }
            else{
               pdf.text(address, 100, 22, {align: 'center'});
            }
            currentDataRow = 0;
            newRow3Y = 40.5;
            let selectedPricingTier = String(viewerState.selectedPricingTier);
            let totalPrice = 0;

            //Add total site summary header
            const totalSiteSummaryHeaders = ['SLOPE', 'ROUNDED', 'PRICE'];
            pdf.setFillColor(91, 138, 190); // blue background
            pdf.rect(0, newRow3Y, customPageWidth, headerRowHeight, 'FD');
            pdf.setTextColor(0); // Set text color to black
            pdf.setFont("", 'bold');
            pdf.text('TOTAL SITE SUMMARY', customPageWidth / 2, newRow3Y + 1 + headerRowHeight / 2, {align: 'center'});

            //Add total site summary sub-headers with blue background
            for (let i = 0; i < totalSiteSummaryHeaders.length; i++) {
                let headerY = newRow3Y + headerRowHeight;
                pdf.setFillColor(91, 138, 190); // blue background
                pdf.rect(i * (customPageWidth / totalSiteSummaryHeaders.length), headerY, customPageWidth / totalSiteSummaryHeaders.length, headerRowHeight, 'FD'); // Fill header background and draw border
                pdf.setFontSize(7);
                const textHeader = splitText(totalSiteSummaryHeaders[i], 15);
                textHeader.forEach((line) => {
                    pdf.text(line, (i * customPageWidth / totalSiteSummaryHeaders.length) + 35, headerY + 7, {align: 'center'});
                    headerY += 2.5;
                });
                if(i === totalSiteSummaryHeaders.length - 1){
                    if(selectedPricingTier !== 'undefined'){
                        pdf.text("SELECTED TIER: " + selectedPricingTier.toUpperCase(), (i * customPageWidth / totalSiteSummaryHeaders.length) + 35, headerY + 9, {align: 'center'});
                    }
                    else{
                        pdf.text("PRICING TIER NOT SELECTED", (i * customPageWidth / totalSiteSummaryHeaders.length) + 35, headerY + 9, {align: 'center'});
                    }
                }
            }
            pdf.setFont("", 'normal');

            roofSquareArr.length = 0;
            let cellData, currentCellX, textY;
            colWidth = customPageWidth / totalSiteSummaryHeaders.length
            // Loop through dataArray for the third section


            for (let rowIndex = 0; rowIndex < 3; rowIndex++) { //rowIndex < 3 because this table will always have 3 rows
                //Transfer data from roof square objects (slope + pricing) to array
                if (roofSquarePricingData && 
                    roofSquarePricingData.roundingData && 
                    roofSquarePricingData.roundingData[rowIndex].slope ) {
                
                        roofSquareArr[0] = roofSquarePricingData.roundingData[rowIndex].slope;
                        roofSquareArr[1] = formatSiteSummaryRounded(
                            roofSquarePricingData.roundingData[rowIndex].rounded,
                            rowIndex === 2,
                        );

                        if(selectedPricingTier !== 'undefined'){ //Check if a pricing tier was selected
                            if(rowIndex === 0){  //Low Slope row
                                if(selectedPricingTier.toLowerCase() === 'floor') //Floor pricing tier
                                    roofSquareArr[2] = Number(roofSquarePricingData.priceData.lowSlopePriceResponse.floorPrice).toFixed();
                                else if(selectedPricingTier.toLowerCase() === 'min'){ //Min pricing tier
                                    roofSquareArr[2] = Number(roofSquarePricingData.priceData.lowSlopePriceResponse.minPrice).toFixed();
                                }
                                else if(selectedPricingTier.toLowerCase() === 'standard') //Standard pricing tier
                                    roofSquareArr[2] = Number(roofSquarePricingData.priceData.lowSlopePriceResponse.standardPrice).toFixed();
                                else //Standard + 1 Pricing tier
                                    roofSquareArr[2] = Number(roofSquarePricingData.priceData.lowSlopePriceResponse.standardPlusOnePrice).toFixed();
                            }
                            else if(rowIndex === 1){  //Normal Slope row
                                if(selectedPricingTier.toLowerCase() === 'floor') //Floor pricing tier
                                    roofSquareArr[2] = Number(roofSquarePricingData.priceData.normalSlopePriceResponse.floorPrice).toFixed();
                                else if(selectedPricingTier.toLowerCase() === 'min'){ //Min pricing tier
                                    roofSquareArr[2] = Number(roofSquarePricingData.priceData.normalSlopePriceResponse.minPrice).toFixed();
                                }
                                else if(selectedPricingTier.toLowerCase() === 'standard') //Standard pricing tier
                                    roofSquareArr[2] = Number(roofSquarePricingData.priceData.normalSlopePriceResponse.standardPrice).toFixed();
                                else //Standard + 1 Pricing tier
                                    roofSquareArr[2] = Number(roofSquarePricingData.priceData.normalSlopePriceResponse.standardPlusOnePrice).toFixed();
                            }
                            else{ //Total row
                                roofSquareArr[2] = totalPrice;
                            }
                            totalPrice += Number(roofSquareArr[2]); //Calculate total price of low and normal
                            roofSquareArr[2] = "$" + Number(roofSquareArr[2]).toLocaleString();
                        }

                        // Set the font size for rows
                        pdf.setFontSize(10);
                        // Calculate the Y position for the current row
                        currentRowY = newRow3Y + (headerRowHeight * 2) + currentDataRow * contentRowHeight;
                        pdf.rect(0, currentRowY, customPageWidth / totalSiteSummaryHeaders.length, contentRowHeight, 'FD');

                        // Set the border color for rows to white
                        pdf.setDrawColor(255, 255, 255);

                        // Alternate row background colors (light grey)
                        if (currentDataRow % 2 === 0) {
                            pdf.setFillColor(179, 178, 177); // Light grey
                        } else {
                            pdf.setFillColor(202, 200, 200); // White
                        }

                        // Add row background color
                        pdf.rect(0, currentRowY, customPageWidth, contentRowHeight, 'FD');

                        // Set the border for the current row
                        pdf.rect(0, currentRowY, customPageWidth, contentRowHeight);

                        for (let colIndex = 0; colIndex < roofSquareArr.length; colIndex++) {

                            cellData = roofSquareArr[colIndex];

                            // Calculate the X position for the current cell
                            currentCellX = colIndex * colWidth;
                            textY = currentRowY + contentRowHeight / 2;

                            // Add cell data (dummy text)
                            pdf.text(cellData.toString(), currentCellX + (colWidth / 2), textY + 1, {align: 'center'});

                            // Add white borders to the cell
                            pdf.rect(currentCellX * 2, currentRowY, colWidth, contentRowHeight);
                        }

                        currentDataRow++; // Increment the current row count

                        // Check if there's enough space for the next row on the current page
                        if (currentDataRow >= maxRowsPerDataPage) {
                            addNewPageForRoofSquares();
                            currentDataRow = 0; // Reset the current row count on the new page
                        }
                }
            }
        }

        return pdf;
    } catch (error) {
        logger.error(error, 'Error creating site report');
        return false;
    }
};

function transformDataToObjectArray(data : allTreeDataType[]) {
    const result = [];
    let currentRow : string[] = [];

    data.forEach((item, index : number) => {
        // Add treeName, treeHeight, and treeDiameter, or an empty string if they are missing
        currentRow.push(
            item.treeName || '',
            item.treeHeight ? item.treeHeight.toString() + "'" : '',
            item.treeDiameter ? item.treeDiameter.toString() + "'" : ''
        );

        // Every 4 trees, start a new row
        if ((index + 1) % 4 === 0) {
            result.push(currentRow);
            currentRow = [];
        }
    });

    // Add the last row if it has any trees
    if (currentRow.length > 0) {
        // Fill the remaining elements with empty strings if the last row has less than 4 trees
        while (currentRow.length < 12) {
            currentRow.push('');
        }
        result.push(currentRow);
    }

    return result;
}
